import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import idx from "idx";
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import MultiSelect from "../../components/inputs/multi-select";
import TargetAccountSelector from "../accounting/target-account-selector";
import SyncInvoicesOverrides from "./sync-invoices-overrides";
import CustomerSyncStatusPanel from "./customer-sync-status-panel";
import CustomTable from "../../components/table/table";
import { getProjectList } from '../../actions/project-management.action';
import { useAccountingMetadata } from '../../hooks/useAccountingMetadata';
import { useInvoiceSync } from './hooks/useInvoiceSync';
import { getSyncInvoicesColumns } from './sync-invoices-columns';
import { toDecimal } from '../../util/decimal-utils';
import './sync-invoices-modal.scss';
import { fetchAccountingConnections } from "../../slice/accounting.slice";

const invoiceStatusItems = [
    { value: "DRAFT", key: "Draft", label: "Draft" },
    { value: "SUBMITTED", key: "Submitted", label: "Submitted" },
    { value: "AUTHORISED", key: "Authorised", label: "Authorised" }
];

let cachedProjects = null;
let cachedProjectsPromise = null;

export default function SyncInvoicesModal({
    show,
    onHide,
    entityType, // 'invoice' | 'bill'
    projectId: propProjectId, // Optional: lock to specific project
    projectName: propProjectName,
    connectionId,
    onSyncSuccess,
    fixedBillTab,
    hideBillTabs
}) {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.userData);
    const accountId = idx(user, _ => _.selected_account.id);
    const userId = idx(user, _ => _.id);

    const reduxConnections = useSelector(state => state.accounting?.connections || []);
    const [connections, setConnections] = useState([]);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProjectIds, setSelectedProjectIds] = useState(propProjectId ? [propProjectId] : []);
    const [loadingProjects, setLoadingProjects] = useState(false);
    const selectedConnectionId = selectedConnection?.id;

    // Dynamic dropdown metadata options via custom hook
    const {
        accountsList,
        taxRatesList,
        invoiceStatusesList,
        poStatusesList,
        loadingMetadata
    } = useAccountingMetadata(selectedConnection?.id);

    const [invoiceStatus, setInvoiceStatus] = useState("DRAFT");
    const [purchaseOrderStatus, setPurchaseOrderStatus] = useState("DRAFT");
    const [lineAmountTypes, setLineAmountTypes] = useState("Exclusive");
    const [accountCode, setAccountCode] = useState("200");
    const [expenseAccountCode, setExpenseAccountCode] = useState("400");
    const [cogsAccountCode, setCogsAccountCode] = useState("400");
    const [contractorAccountCode, setContractorAccountCode] = useState("400");
    const [billTab, setBillTab] = useState(fixedBillTab || "supplier"); // 'supplier' | 'contractor'
    const [taxType, setTaxType] = useState("");
    const [expenseTaxType, setExpenseTaxType] = useState("");
    const [squashLineItems, setSquashLineItems] = useState(false);
    const [exchangeRate, setExchangeRate] = useState("1.0");

    const platformName = selectedConnection
        ? (selectedConnection.display_name || selectedConnection.platform_connection_name || 'Accounting')
        : 'Accounting';

    const getSettingsOverride = useCallback(() => {
        const activeExpenseAccountCode = entityType === 'bill'
            ? (billTab === 'contractor' ? contractorAccountCode : cogsAccountCode)
            : undefined;

        return {
            invoiceStatus,
            purchaseOrderStatus,
            lineAmountTypes,
            accountCode: entityType === 'invoice' ? accountCode : undefined,
            expenseAccountCode: activeExpenseAccountCode,
            cogsAccountCode: entityType === 'bill' ? cogsAccountCode : undefined,
            contractorAccountCode: entityType === 'bill' ? contractorAccountCode : undefined,
            taxType: entityType === 'invoice' ? taxType : undefined,
            expenseTaxType: entityType === 'bill' ? expenseTaxType : undefined,
            squashLineItems,
            exchangeRate: toDecimal(exchangeRate || 1.0).toNumber()
        };
    }, [
        entityType,
        billTab,
        contractorAccountCode,
        cogsAccountCode,
        invoiceStatus,
        purchaseOrderStatus,
        lineAmountTypes,
        accountCode,
        taxType,
        expenseTaxType,
        squashLineItems,
        exchangeRate
    ]);

    // Use customized hook for syncing, progress state, and polling
    const {
        invoices,
        loadingInvoices,
        selectedIds,
        syncing,
        syncProgress,
        syncLogs,
        checkingCustomerLink,
        customersSyncStatus,
        syncHappened,
        forceSync,
        setForceSync,
        fetchInvoices,
        handleSelectAll,
        handleSelectRow,
        handleSyncCustomer,
        handleExecuteSync,
        handleCancelSync
    } = useInvoiceSync({
        selectedConnection,
        entityType,
        selectedProjectIds,
        billTab,
        projects,
        accountId,
        userId,
        userName: user?.name,
        getSettingsOverride
    });

    const entitlements = useSelector((state) => state.user.entitlements) || {};

    const isPushAllowed = selectedConnection ? (() => {
        if (selectedConnection.platform === 'xero') return entitlements.xeroPush !== false && entitlements.accountingPush !== false;
        if (selectedConnection.platform === 'quickbooks') return entitlements.qboPush !== false && entitlements.accountingPush !== false;
        if (selectedConnection.platform === 'myob') return entitlements.myobPush !== false && entitlements.accountingPush !== false;
        return entitlements.accountingPush !== false;
    })() : true;

    // Load active accounting connections
    const fetchConnections = useCallback(async () => {
        if (!accountId) return;
        try {
            let activeConns = reduxConnections;
            if (!activeConns || activeConns.length === 0) {
                activeConns = await dispatch(fetchAccountingConnections(false, false));
            }

            const filteredConns = activeConns.filter(c => {
                if (c.platform === 'xero' && entitlements.xeroIntegration === false) return false;
                if (c.platform === 'quickbooks' && entitlements.qboIntegration === false) return false;
                if (c.platform === 'myob' && entitlements.myobIntegration === false) return false;
                return true;
            });

            setConnections(filteredConns);

            const isConnPushAllowed = (c) => {
                if (!c) return false;
                if (c.platform === 'xero') return entitlements.xeroPush !== false;
                if (c.platform === 'quickbooks') return entitlements.qboPush !== false;
                if (c.platform === 'myob') return entitlements.myobPush !== false;
                return entitlements.accountingPush !== false;
            };

            let initialConn = null;
            if (connectionId) {
                initialConn = filteredConns.find(c => String(c.id) === String(connectionId));
            }
            if (!initialConn) {
                initialConn = filteredConns.find(c => c.is_default && isConnPushAllowed(c))
                    || filteredConns.find(c => isConnPushAllowed(c))
                    || filteredConns.find(c => c.is_default)
                    || filteredConns[0];
            }
            setSelectedConnection(initialConn || null);
        } catch (err) {
            console.error("Failed to load platform connections:", err);
        }
    }, [accountId, connectionId, entitlements, reduxConnections, dispatch]);

    // Load projects list
    const fetchProjects = useCallback(async () => {
        if (propProjectId) return; // Skip if locked to a project
        if (cachedProjects) {
            setProjects(cachedProjects);
            return;
        }
        if (cachedProjectsPromise) {
            try {
                const list = await cachedProjectsPromise;
                setProjects(Array.isArray(list) ? list : []);
                return;
            } catch (err) {
                cachedProjectsPromise = null;
            }
        }
        setLoadingProjects(true);
        try {
            cachedProjectsPromise = dispatch(getProjectList(null, "PROJECT"));
            const list = await cachedProjectsPromise;
            const projectsList = Array.isArray(list) ? list : [];
            cachedProjects = projectsList;
            setProjects(projectsList);
        } catch (err) {
            console.error("Failed to load projects list:", err);
            cachedProjectsPromise = null;
        } finally {
            setLoadingProjects(false);
        }
    }, [dispatch, propProjectId]);

    useEffect(() => {
        if (show) {
            fetchConnections();
            fetchProjects();
            const nextProjectIds = propProjectId ? [propProjectId] : [];
            const isSame = selectedProjectIds.length === nextProjectIds.length &&
                selectedProjectIds.every((val, index) => val === nextProjectIds[index]);
            if (!isSame) {
                setSelectedProjectIds(nextProjectIds);
            }
            setBillTab(fixedBillTab || 'supplier');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, propProjectId, fixedBillTab]);

    useEffect(() => {
        if (show && selectedProjectIds) {
            fetchInvoices();
        }
    }, [show, selectedProjectIds, fetchInvoices]);

    useEffect(() => {
        if (show && selectedConnectionId) {
            const freshConn = reduxConnections.find(c => c.id === selectedConnectionId) || selectedConnection;
            const settings = freshConn?.settings || {};
            setInvoiceStatus(settings.invoiceStatus || "DRAFT");
            setPurchaseOrderStatus(settings.purchaseOrderStatus || "DRAFT");
            setLineAmountTypes(settings.lineAmountTypes || "Exclusive");
            setAccountCode(settings.accountCode || "");
            setExpenseAccountCode(settings.expenseAccountCode || settings.cogsAccountCode || "");
            setCogsAccountCode(settings.cogsAccountCode || settings.expenseAccountCode || "");
            setContractorAccountCode(settings.contractorAccountCode || settings.expenseAccountCode || "");
            setTaxType(settings.taxType || "");
            setExpenseTaxType(settings.expenseTaxType || "");
            const defaultSquash = entityType === 'invoice'
                ? (settings.squashLinesInvoices !== undefined ? settings.squashLinesInvoices : (settings.squashLineItems !== undefined ? settings.squashLineItems : false))
                : (settings.squashLinesBills !== undefined ? settings.squashLinesBills : (settings.squashLineItems === true));
            setSquashLineItems(defaultSquash);
        } else if (!selectedConnection) {
            setSquashLineItems(false);
        }
    }, [show, reduxConnections, selectedConnection, selectedConnectionId, entityType]);

    useEffect(() => {
        if (!selectedConnection || loadingMetadata) return;
        const settings = selectedConnection.settings || {};

        if (accountsList.length > 0) {
            if (!settings.accountCode && (!accountCode || !accountsList.some(a => a.code === accountCode))) {
                const salesAcc = accountsList.find(a => {
                    const name = (a.name || '').toLowerCase();
                    const type = (a.type || '').toLowerCase();
                    return name.includes('sales') || name.includes('revenue') || type.includes('revenue') || type.includes('sales');
                });
                if (salesAcc) setAccountCode(salesAcc.code);
                else if (accountsList[0]) setAccountCode(accountsList[0].code);
            }
            if (!settings.cogsAccountCode && (!cogsAccountCode || !accountsList.some(a => a.code === cogsAccountCode))) {
                const cogsAcc = accountsList.find(a => {
                    const name = (a.name || '').toLowerCase();
                    const type = (a.type || '').toLowerCase();
                    return name.includes('cost of goods sold') || name.includes('cogs') || name.includes('cost of sales') || type.includes('cogs') || type.includes('costofgoods');
                });
                if (cogsAcc) setCogsAccountCode(cogsAcc.code);
                else if (accountsList[0]) setCogsAccountCode(accountsList[0].code);
            }
            if (!settings.contractorAccountCode && (!contractorAccountCode || !accountsList.some(a => a.code === contractorAccountCode))) {
                const contractorAcc = accountsList.find(a => {
                    const name = (a.name || '').toLowerCase();
                    return name.includes('subcontractor') || name.includes('sub-contractor') || name.includes('contractor') || name.includes('cost of labor') || name.includes('labor') || name.includes('labour');
                });
                if (contractorAcc) setContractorAccountCode(contractorAcc.code);
                else if (accountsList[0]) setContractorAccountCode(accountsList[0].code);
            }
        }

        if (taxRatesList.length > 0) {
            if (!taxType || !taxRatesList.some(t => (t.code || t.id || t.name) === taxType)) {
                const matchedTax = settings.taxType ? taxRatesList.find(t => t.code === settings.taxType || t.id === settings.taxType || t.name === settings.taxType) : null;
                if (matchedTax) {
                    setTaxType(matchedTax.code || matchedTax.id || matchedTax.name);
                } else if (!taxType) {
                    const defaultTax = taxRatesList.find(t => String(t.name || t.code).toLowerCase().includes('tax exempt') || String(t.name || t.code).toLowerCase().includes('exempt')) || taxRatesList[0];
                    if (defaultTax) setTaxType(defaultTax.code || defaultTax.id || defaultTax.name);
                }
            }
            if (!expenseTaxType || !taxRatesList.some(t => (t.code || t.id || t.name) === expenseTaxType)) {
                const matchedExpenseTax = settings.expenseTaxType ? taxRatesList.find(t => t.code === settings.expenseTaxType || t.id === settings.expenseTaxType || t.name === settings.expenseTaxType) : null;
                if (matchedExpenseTax) {
                    setExpenseTaxType(matchedExpenseTax.code || matchedExpenseTax.id || matchedExpenseTax.name);
                } else if (!expenseTaxType) {
                    const defaultTax = taxRatesList.find(t => String(t.name || t.code).toLowerCase().includes('tax exempt') || String(t.name || t.code).toLowerCase().includes('exempt')) || taxRatesList[0];
                    if (defaultTax) setExpenseTaxType(defaultTax.code || defaultTax.id || defaultTax.name);
                }
            }
        }
    }, [selectedConnection, accountsList, taxRatesList, loadingMetadata]);

    const handleClose = () => {
        if (syncHappened && onSyncSuccess) {
            onSyncSuccess();
        }
        onHide();
    };

    // Get unique customers from invoices for linkage check
    const uniqueCustomers = React.useMemo(() => {
        const seen = new Set();
        const list = [];
        invoices.forEach(inv => {
            if (inv.customerId && !seen.has(inv.customerId)) {
                seen.add(inv.customerId);
                list.push({
                    id: inv.customerId,
                    name: inv.customerName
                });
            }
        });
        return list;
    }, [invoices]);

    return (
        <Modal
            show={show}
            onHide={syncing ? undefined : handleClose}
            size="xl"
            centered
            backdrop={syncing ? 'static' : true}
            keyboard={!syncing}
            dialogClassName="sync-invoices-modal"
            data-cy="sync-invoices-modal-container"
        >
            <Modal.Header closeButton={!syncing} className="border-bottom-0 pb-0">
                <Modal.Title className="text-uppercase font-weight-bold brand-color-3" style={{ fontSize: '1.25rem' }} data-cy="sync-invoices-modal-title">
                    {entityType === 'invoice'
                        ? 'Sync Progress Claims (AR)'
                        : billTab === 'contractor'
                            ? 'Sync Contractor Bills (AP)'
                            : 'Sync Supplier Bills (AP)'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <TargetAccountSelector
                    connections={connections}
                    selectedConnection={selectedConnection}
                    onChange={setSelectedConnection}
                    disabled={syncing}
                    isPushModal={true}
                    dataCyDropdown="sync-invoices-connection-dropdown"
                />

                <Row className="mb-3 align-items-start">
                    <Col md={entityType === 'invoice' && uniqueCustomers.length > 0 ? 6 : 12}>
                        {!propProjectId ? (
                            loadingProjects ? (
                                <div className="py-2 small text-muted"><Spinner size="sm" animation="border" className="me-2" />Loading Projects...</div>
                            ) : (
                                <MultiSelect
                                    label="Select Projects"
                                    placeholder="Choose Projects"
                                    isMulti={true}
                                    className="mb-0"
                                    data-cy="sync-invoices-projects-dropdown"
                                    isDisabled={syncing}
                                    items={[
                                        { value: "ALL", label: "ALL (All Projects)", key: "ALL" },
                                        ...projects.map(p => ({ value: p.id, label: p.name, key: p.id }))
                                    ]}
                                    value={[
                                        { value: "ALL", label: "ALL (All Projects)", key: "ALL" },
                                        ...projects.map(p => ({ value: p.id, label: p.name, key: p.id }))
                                    ].filter(opt => selectedProjectIds.includes(opt.value))}
                                    onChangeHandler={(selectedOptions) => {
                                        const selectedVals = (selectedOptions || []).map(o => o.value);

                                        if (selectedVals.includes("ALL") && !selectedProjectIds.includes("ALL")) {
                                            setSelectedProjectIds(["ALL", ...projects.map(p => p.id)]);
                                        } else if (!selectedVals.includes("ALL") && selectedProjectIds.includes("ALL")) {
                                            setSelectedProjectIds([]);
                                        } else {
                                            const withoutAll = selectedVals.filter(v => v !== "ALL");
                                            if (withoutAll.length === projects.length) {
                                                setSelectedProjectIds(["ALL", ...withoutAll]);
                                            } else {
                                                setSelectedProjectIds(withoutAll);
                                            }
                                        }
                                    }}
                                />
                            )
                        ) : (
                            <div className="border rounded p-2 bg-light d-flex flex-column justify-content-center project-select-card">
                                <span className="font-weight-bold text-muted small text-uppercase project-select-title">Project</span>
                                <div className="fw-bold text-truncate project-select-value">
                                    {propProjectName || projects.find(p => String(p.id) === String(propProjectId))?.name || invoices[0]?.projectName || 'Selected Project'}
                                </div>
                            </div>
                        )}
                    </Col>
                    {entityType === 'invoice' && uniqueCustomers.length > 0 && (
                        <Col md={6}>
                            <CustomerSyncStatusPanel
                                uniqueCustomers={uniqueCustomers}
                                customersSyncStatus={customersSyncStatus}
                                checkingCustomerLink={checkingCustomerLink}
                                selectedConnection={selectedConnection}
                                handleSyncCustomer={handleSyncCustomer}
                                syncing={syncing}
                            />
                        </Col>
                    )}
                </Row>

                {entityType === 'bill' && !hideBillTabs && (
                    <div className="d-flex mb-3 border-bottom" style={{ gap: 0 }}>
                        <button
                            className={`btn btn-sm px-4 py-2 fw-bold text-uppercase border-0 border-bottom border-3 rounded-0 ${billTab === 'supplier'
                                ? 'text-primary border-primary'
                                : 'text-muted border-transparent'
                                }`}
                            style={{ borderBottomWidth: billTab === 'supplier' ? '3px' : '3px', borderBottomStyle: 'solid', borderBottomColor: billTab === 'supplier' ? 'var(--bs-primary)' : 'transparent' }}
                            onClick={() => { if (!syncing) setBillTab('supplier'); }}
                            disabled={syncing}
                            data-cy="bill-tab-supplier"
                        >
                            Supplier
                        </button>
                        <button
                            className={`btn btn-sm px-4 py-2 fw-bold text-uppercase border-0 border-bottom border-3 rounded-0 ${billTab === 'contractor'
                                ? 'text-primary border-primary'
                                : 'text-muted border-transparent'
                                }`}
                            style={{ borderBottomWidth: '3px', borderBottomStyle: 'solid', borderBottomColor: billTab === 'contractor' ? 'var(--bs-primary)' : 'transparent' }}
                            onClick={() => { if (!syncing) setBillTab('contractor'); }}
                            disabled={syncing}
                            data-cy="bill-tab-contractor"
                        >
                            Contractor
                        </button>
                    </div>
                )}

                <SyncInvoicesOverrides
                    selectedConnection={selectedConnection}
                    entityType={entityType}
                    invoiceStatus={invoiceStatus}
                    setInvoiceStatus={setInvoiceStatus}
                    purchaseOrderStatus={purchaseOrderStatus}
                    setPurchaseOrderStatus={setPurchaseOrderStatus}
                    squashLineItems={squashLineItems}
                    setSquashLineItems={setSquashLineItems}
                    accountCode={accountCode}
                    setAccountCode={setAccountCode}
                    expenseAccountCode={expenseAccountCode}
                    setExpenseAccountCode={setExpenseAccountCode}
                    cogsAccountCode={cogsAccountCode}
                    setCogsAccountCode={setCogsAccountCode}
                    contractorAccountCode={contractorAccountCode}
                    setContractorAccountCode={setContractorAccountCode}
                    billTab={billTab}
                    taxType={taxType}
                    setTaxType={setTaxType}
                    expenseTaxType={expenseTaxType}
                    setExpenseTaxType={setExpenseTaxType}
                    accountsList={accountsList}
                    taxRatesList={taxRatesList}
                    invoiceStatusesList={invoiceStatusesList}
                    poStatusesList={poStatusesList}
                    invoiceStatusItems={invoiceStatusesList.length > 0 ? invoiceStatusesList : invoiceStatusItems}
                    loadingMetadata={loadingMetadata}
                    syncing={syncing}
                />

                {selectedProjectIds.length > 0 && (
                    <div className="invoice-sync-selection-list mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="font-weight-bold text-dark small text-uppercase override-accordion-title">
                                Available {entityType === 'invoice' ? 'Claims' : billTab === 'contractor' ? 'Contractor Bills' : 'Supplier Invoices'} ({invoices.length})
                            </span>
                            {selectedIds.length > 0 && (
                                <span className="badge bg-primary">{selectedIds.length} Selected</span>
                            )}
                        </div>

                        {invoices.length === 0 && !loadingInvoices ? (
                            <div className="text-center py-5 text-muted border rounded bg-light">
                                No exportable {entityType === 'invoice' ? 'claims' : billTab === 'contractor' ? 'contractor bills' : 'PO invoices'} found for the selected projects.
                            </div>
                        ) : (
                            <div className="table-responsive border rounded overflow-hidden ">
                                <CustomTable
                                    columns={getSyncInvoicesColumns({
                                        invoices,
                                        selectedIds,
                                        syncing,
                                        handleSelectAll,
                                        handleSelectRow,
                                        entityType,
                                        propProjectId,
                                        billTab,
                                        syncLogs
                                    })}
                                    data={invoices}
                                    isLoading={loadingInvoices || syncing}
                                    toolbar={true}
                                    itemsPerPageCustom={false}
                                    itemsPerPage={10}
                                    exportButton={false}
                                    searchPlaceholderCustom="Search items..."
                                />
                            </div>
                        )}
                    </div>
                )}

                {syncing && (
                    <div className="mt-3 p-3 bg-light rounded border border-primary-subtle d-flex align-items-center justify-content-center">
                        <Spinner size="sm" animation="border" className="me-2 text-primary" />
                        <span className="fw-bold text-primary small">
                            Pushing invoices: {syncProgress.current} of {syncProgress.total}...
                        </span>
                    </div>
                )}

                {!isPushAllowed && selectedConnection && (
                    <div className="alert alert-warning mt-3 mb-0 py-2 px-3 small">
                        Push operations are disabled for {platformName} under your account's subscription plan.
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-top-0 pt-0">
                <div className="me-auto d-flex align-items-center">
                    {selectedConnection && invoices.length > 0 && (
                        selectedIds.length === 0 ? (
                            <span className="small text-muted me-3">Selected: <strong>All (Full Push)</strong></span>
                        ) : (
                            <span className="small text-muted me-3">Selected: <strong>{selectedIds.length}</strong> of <strong>{invoices.length}</strong> item(s)</span>
                        )
                    )}
                    {selectedConnection && invoices.length > 0 && (
                        <Form.Check
                            type="checkbox"
                            id="force-sync-checkbox"
                            label={<span className="small text-dark fw-semibold">Force Re-sync All</span>}
                            checked={forceSync}
                            onChange={(e) => setForceSync(e.target.checked)}
                            disabled={syncing}
                        />
                    )}
                </div>
                {syncing ? (
                    <Button variant="danger" onClick={handleCancelSync} data-cy="sync-invoices-cancel-btn">
                        Cancel Sync
                    </Button>
                ) : (
                    <Button variant="secondary" onClick={handleClose} disabled={syncing} data-cy="sync-invoices-close-btn">
                        Close
                    </Button>
                )}
                <Button
                    variant="primary"
                    onClick={handleExecuteSync}
                    disabled={syncing || loadingMetadata || !selectedConnection || invoices.length === 0 || !isPushAllowed}
                    data-cy="sync-invoices-submit-btn"
                >
                    {syncing ? 'Pushing...' : loadingMetadata ? 'Loading Settings...' : !isPushAllowed ? 'Push Disabled' : (selectedIds.length === 0 ? `Push All to ${platformName}` : `Push Selected to ${platformName}`)}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
